import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import '../components/header.css';

const langCodeToFlag = {
  en: '🇺🇸',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  ja: '🇯🇵',
};


export default function Header() {
  const [user, setUser] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const stored = localStorage.getItem('selectedLanguage');
    return langCodeToFlag[stored] ? stored : 'en';
  });
  const [showLangNotice, setShowLangNotice] = useState(false);
  const menuRef = useRef();
  const languageRef = useRef();
  const noticeTimerRef = useRef(null);

  // 👂 Listen for auth state change and fetch avatar
  useEffect(() => {
    const fetchUserData = async (currentUser) => {
      const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_avatar, user_photo, avatar_or_pic')
      .eq('user_id', currentUser.id)
      .single();
    
    if (profile) {
      if (profile.avatar_or_pic === 'photo' && profile.user_photo) {
        setAvatarUrl(profile.user_photo); // use uploaded/profile pic
      } else if (profile.user_avatar) {
        setAvatarUrl(profile.user_avatar); // fallback to avatar
      } else {
        setAvatarUrl(null);
      }
      
    } else {
      console.log('No profile found');
    }
    
    };

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const loggedInUser = session?.user || null;
      setUser(loggedInUser);
      if (loggedInUser) fetchUserData(loggedInUser);
    });

    // One-time fetch on initial load
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        setUser(data.user);
        fetchUserData(data.user);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // 🔒 Log out
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAvatarUrl(null);
    setMenuOpen(false);
    window.location.href = '/';
  };

  // 🌐 Language change
  const handleLanguageSelect = (langCode) => {
    // Clear any previous timer so it doesn't overlap
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }

    // Show popup
    setShowLangNotice(true);

    // Hide popup after 2s
    noticeTimerRef.current = setTimeout(() => {
      setShowLangNotice(false);
    }, 2000);

    // Save selected language
    setSelectedLanguage(langCode);
    localStorage.setItem('selectedLanguage', langCode);
    window.dispatchEvent(
      new StorageEvent('storage', { key: 'selectedLanguage', newValue: langCode })
    );

    setLanguageMenuOpen(false);
  };


  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close language menu on outside click
  useEffect(() => {
    const handleClickOutsideLang = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };
    if (languageMenuOpen) document.addEventListener('mousedown', handleClickOutsideLang);
    return () => document.removeEventListener('mousedown', handleClickOutsideLang);
  }, [languageMenuOpen]);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="header-container">
      {/* Left Side */}
      <div className="header-left">
        <Link to="/" className="logo">
          <img src={require('../assets/s2slogo.png')} alt="Logo" className="logo-img" />
        </Link>
        <Link to="/about" className="nav-link">About</Link>
        <Link to="/suggestionbox" className="nav-link">Suggestion Box</Link>
        <Link to="/openevents" className="nav-link">Open Invites</Link>

      </div>

      {/* Right Side */}
      <div className="header-right">
        {/* Language Flag */}
        <div className="language-wrapper" ref={languageRef}>
          <button
            className="language-button"
            onClick={() => {
              // show the “coming soon” popup; no dropdown for now
              if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
              setShowLangNotice(true);
              noticeTimerRef.current = setTimeout(() => setShowLangNotice(false), 2000);
            }}
            aria-label="Language selector (coming soon)"
          >
            {langCodeToFlag[selectedLanguage] ?? '🇺🇸'}
          </button>

        </div>


        {/* Profile Capsule */}
        <div
          ref={menuRef}
          className="profile-capsule"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {/* Avatar Image or Default */}
          <div className="avatar-profile">
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="avatar-img" />
            ) : (
              '👤' // Placeholder if no avatar
            )}
          </div>

          {menuOpen && (
            <div className="dropdown-menu">
              {user ? (
                <>
                  <Link to="/dashboard" className="dropdown-item">Dashboard</Link>
                  <Link to="/settings" className="dropdown-item">Account Settings</Link>
                  <div onClick={handleLogout} className="dropdown-item">Log Out</div>
                </>
              ) : (
                <>
                  <Link to="/login" className="dropdown-item">Login</Link>
                  <Link to="/login?mode=signup" className="dropdown-item">Sign Up</Link>
                </>
              )}
              <Link to="/helppage" className="dropdown-item">Help</Link>
              <Link to="/faq" className="dropdown-item">FAQ</Link>
            </div>
          )}
        </div>
      </div> 
    </div>
  );
}
